'use client'

import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/button'
import { SvgOutlinedCheckmark } from '@/icons/svg-outlined-checkmark'
import { SvgOutlinedCopy } from '@/icons/svg-outlined-copy'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { copyToClipboard } from '@/utils/window/copy-to-clipboard'

interface ICopyCodeProps {
    code: string
    // On mobile show only the copy icon (label appears from `md` up).
    iconOnlyOnMobile?: boolean
}

export const CopyCode = ({ code, iconOnlyOnMobile }: ICopyCodeProps) => {
    const t = useTranslations()

    const [isCopySuccess, setIsCopySuccess] = useState(false)

    const onCopyClick = useCallback(() => {
        if (!code) return

        copyToClipboard({
            text: code,
            onError: () => alert(t('shared.copyCode.error')),
            onSuccess: () => {
                setIsCopySuccess(true)
            },
        })
    }, [code, t])

    useEffect(() => {
        if (!isCopySuccess) return

        const timeout = setTimeout(() => {
            setIsCopySuccess(false)
        }, 4000)

        return () => clearTimeout(timeout)
    }, [isCopySuccess])

    return (
        <div className="flex items-center p-2.5 justify-between bg-white text-black rounded-s w-full min-h-16 flex-wrap">
            <p className="font-bold text-[20px] ml-2.5">{code}</p>

            <Button
                onClick={onCopyClick}
                styleType="secondary"
                backgroundColor={isCopySuccess ? COLORS.neotaste : COLORS.black}
                contentColor="white"
            >
                <div className="px-1.5 flex items-center">
                    <AnimatePresence mode="wait">
                        {!isCopySuccess && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.04, ease: 'easeOut' },
                                }}
                                key="prev-chevron-no-success"
                                className="rotate-180"
                            >
                                <SvgOutlinedCopy color="white" size={16} />
                            </motion.div>
                        )}

                        {isCopySuccess && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.04, ease: 'easeOut' },
                                }}
                                key="prev-chevron-success"
                            >
                                <SvgOutlinedCheckmark color={COLORS.white} size={16} />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <span className={cn('ml-2', iconOnlyOnMobile && 'hidden md:inline')}>
                        {t(isCopySuccess ? 'shared.copyCode.copied' : 'shared.copyCode.copy')}
                    </span>
                </div>
            </Button>
        </div>
    )
}
